import React from 'react';
import { useLocale } from './LocaleProvider';
import { type Locale } from './locale';
import { localizeHref as localizePath } from './availability';
import assetMap from './asset-map.json';

export type TranslationMap = Readonly<Record<string, string>>;
export const normalizeCopy = (value: string) => value.replace(/\s+/g, ' ').trim();
export function translateCopy(value: string, messages: TranslationMap): string {
  const key = normalizeCopy(value);
  const translated = messages[key];
  if (translated === undefined || !key) return value;
  return `${/^\s/.test(value) ? ' ' : ''}${translated}${/\s$/.test(value) ? ' ' : ''}`;
}

const textProps = new Set(['title', 'description', 'label', 'placeholder', 'alt', 'aria-label', 'aria-description', 'data-ga-text', 'keywords', 'answer', 'question', 'text', 'initialMessage']);
function translateStructured(value: unknown, messages: TranslationMap, locale: Locale): unknown {
  if (Array.isArray(value)) return value.map(item => translateStructured(item, messages, locale));
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [key,
    typeof child === 'string' && ['question', 'answer', 'name', 'description', 'headline'].includes(key) ? translateCopy(child, messages)
      : typeof child === 'string' && key === 'url' ? localizePath(child, locale) : child]));
}
const maps = assetMap as Record<string, string>;
export function localizedAsset(src: string, locale: Locale) { return locale === 'pt-BR' ? maps[src] || src : src; }

/** Trusted repository HTML only. Never rewrites code/pre contents or external links. */
export function localizeHtml(html: string, locale: Locale): string {
  return html.split(/(<pre\b[\s\S]*?<\/pre>|<code\b[\s\S]*?<\/code>)/gi).map(part => {
    if (/^<(pre|code)\b/i.test(part)) return part;
    return part.replace(/\b(href|src|srcset)=(['"])(.*?)\2/g, (_, attr, quote, value) => {
      if (attr === 'href') return `${attr}=${quote}${localizePath(value, locale)}${quote}`;
      if (attr === 'srcset') {
        const localized = value.split(',').map(candidate => {
          const [url, ...descriptors] = candidate.trim().split(/\s+/);
          return [localizedAsset(url, locale), ...descriptors].join(' ');
        }).join(', ');
        return `${attr}=${quote}${localized}${quote}`;
      }
      return `${attr}=${quote}${localizedAsset(value, locale)}${quote}`;
    });
  }).join('');
}

/** Exact catalog lookup during React rendering, including SSG. No DOM mutation or word substitution. */
export function translateTree(node: React.ReactNode, messages: TranslationMap, locale: Locale): React.ReactNode {
  if (typeof node === 'string') return translateCopy(node, messages);
  if (Array.isArray(node)) return React.Children.map(node, child => translateTree(child, messages, locale));
  if (!React.isValidElement(node)) return node;
  const element = node as React.ReactElement<Record<string, unknown>>;
  const type = element.type;
  if (type === 'code' || type === 'pre' || type === 'script' || type === 'style') return node;
  // Content marked as English (an approved quote) stays English on the Portuguese site.
  if (element.props.lang === 'en' && type !== 'a' && typeof type === 'string') return node;
  const props: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(element.props)) {
    if (key === 'children') props[key] = translateTree(value as React.ReactNode, messages, locale);
    else if (textProps.has(key) && typeof value === 'string') props[key] = translateCopy(value, messages);
    else if (key === 'href' && element.props.lang !== 'en' && element.props.hrefLang !== 'en' && typeof value === 'string') props[key] = localizePath(value, locale);
    else if (key === 'src' && typeof value === 'string') props[key] = localizedAsset(value, locale);
    else if (['faqSchema', 'productSchema', 'articleSchema', 'breadcrumbSchema'].includes(key)) props[key] = translateStructured(value, messages, locale);
  }
  return React.cloneElement(element, props);
}

export function Localize({ children, messages }: { children: React.ReactNode; messages: TranslationMap }) {
  const { locale } = useLocale();
  return <>{locale === 'pt-BR' ? translateTree(children, messages, locale) : children}</>;
}
